import React from 'react';

type Mood = 'happy' | 'thinking' | 'cheer';

// evolution stage 0-7 from level; stage 7 = legendary (lv 67)
export function getEvolution(level: number): number {
  if (level >= 67) return 7;
  return Math.min(Math.floor(level / 10), 7);
}

// size formula for TopBar (base 32px) and profile (base 80px)
export function getTopBarSize(level: number): number {
  return 32 + 4 * getEvolution(level);
}
export function getProfileSize(level: number): number {
  return 80 + 8 * getEvolution(level);
}

interface Props {
  size?: number;
  mood?: Mood;
  wink?: boolean;
  level?: number;
  style?: React.CSSProperties;
}

const Fizzbuzz: React.FC<Props> = ({ size = 80, mood = 'happy', wink = false, level = 1, style }) => {
  const evo = getEvolution(level);
  const eyeY = 42;

  const stripeW = evo >= 2 ? 7 : 6;
  const stripeColor = evo >= 5 ? '#FFD700' : '#e8b450';
  const bodyFill = evo >= 4 ? 'rgba(255,255,255,0.52)' : 'rgba(255,255,255,0.40)';
  const haloFill =
    evo >= 7 ? 'rgba(150,100,255,0.35)' :
    evo >= 5 ? 'rgba(255,215,0,0.28)'   :
               'rgba(232,180,80,0.25)';

  let svgStyle = style;
  if (evo >= 7) {
    svgStyle = { ...style, filter: 'drop-shadow(0 0 10px rgba(150,100,255,0.85))' };
  } else if (evo >= 5) {
    svgStyle = { ...style, filter: 'drop-shadow(0 0 6px rgba(255,215,0,0.75))' };
  }

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      style={svgStyle}
      aria-label={`Fizzbuzz mascot level ${level}`}
    >
      {/* Legendary outer dashed ring (lv 67) */}
      {evo >= 7 && (
        <>
          <circle cx="50" cy="55" r="47" fill="none" stroke="rgba(150,100,255,0.5)" strokeWidth="2.5" strokeDasharray="7 4">
            <animateTransform attributeName="transform" type="rotate" from="0 50 55" to="360 50 55" dur="8s" repeatCount="indefinite" />
          </circle>
          <circle cx="50" cy="55" r="43" fill="none" stroke="rgba(255,200,50,0.45)" strokeWidth="1.5" strokeDasharray="4 6">
            <animateTransform attributeName="transform" type="rotate" from="360 50 55" to="0 50 55" dur="12s" repeatCount="indefinite" />
          </circle>
        </>
      )}

      {/* Glow aura ring (lv 50–66) */}
      {evo >= 5 && evo < 7 && (
        <circle cx="50" cy="55" r="43" fill="rgba(255,215,0,0.08)" stroke="rgba(255,215,0,0.4)" strokeWidth="2">
          <animate attributeName="r" values="43;45;43" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0.5;1" dur="2.4s" repeatCount="indefinite" />
        </circle>
      )}

      {/* Second wing pair — upper/smaller (lv 60+) */}
      {evo >= 6 && (
        <>
          <ellipse cx="18" cy="38" rx="11" ry="15" fill="rgba(190,240,190,0.75)" stroke="#1e1b16" strokeWidth="1.3" transform="rotate(-30 18 38)">
            <animateTransform attributeName="transform" type="rotate" values="-30 18 38;-24 18 38;-30 18 38" dur="1.8s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="82" cy="38" rx="11" ry="15" fill="rgba(190,240,190,0.75)" stroke="#1e1b16" strokeWidth="1.3" transform="rotate(30 82 38)">
            <animateTransform attributeName="transform" type="rotate" values="30 82 38;24 82 38;30 82 38" dur="1.8s" repeatCount="indefinite" />
          </ellipse>
        </>
      )}

      {/* Halo */}
      <circle cx="50" cy="55" r="38" fill={haloFill} />

      {/* Cape (lv 30+) */}
      {evo >= 3 && (
        <path d="M 30 63 Q 40 86, 50 91 Q 60 86, 70 63" fill="#b03030" stroke="#1e1b16" strokeWidth="1.4" opacity="0.88">
          <animateTransform attributeName="transform" type="skewX" values="0;2;0;-2;0" dur="3s" repeatCount="indefinite" />
        </path>
      )}

      {/* Main wings behind body */}
      <ellipse cx="22" cy="48" rx="14" ry="18" fill="rgba(200,220,240,0.85)" stroke="#1e1b16" strokeWidth="1.6" transform="rotate(-18 22 48)">
        {evo >= 6 && (
          <animateTransform attributeName="transform" type="rotate" values="-18 22 48;-12 22 48;-18 22 48" dur="1.4s" repeatCount="indefinite" />
        )}
      </ellipse>
      <ellipse cx="78" cy="48" rx="14" ry="18" fill="rgba(200,220,240,0.85)" stroke="#1e1b16" strokeWidth="1.6" transform="rotate(18 78 48)">
        {evo >= 6 && (
          <animateTransform attributeName="transform" type="rotate" values="18 78 48;12 78 48;18 78 48" dur="1.4s" repeatCount="indefinite" />
        )}
      </ellipse>

      {/* Wing shimmer lines */}
      <path d="M 14 38 Q 22 50, 28 60" stroke="#fff" strokeWidth="1.4" fill="none" opacity="0.7" />
      <path d="M 86 38 Q 78 50, 72 60" stroke="#fff" strokeWidth="1.4" fill="none" opacity="0.7" />

      {/* Body */}
      <circle cx="50" cy="55" r="30" fill={bodyFill} stroke="#1e1b16" strokeWidth="2" />

      {/* Armor plate (lv 40+) */}
      {evo >= 4 && (
        <>
          <path d="M 34 43 Q 50 39, 66 43 L 66 57 Q 50 61, 34 57 Z" fill="rgba(100,120,200,0.28)" stroke="#1e1b16" strokeWidth="1.2" />
          <line x1="44" y1="43" x2="44" y2="57" stroke="#1e1b16" strokeWidth="0.7" opacity="0.5" />
          <line x1="56" y1="43" x2="56" y2="57" stroke="#1e1b16" strokeWidth="0.7" opacity="0.5" />
          {/* Armor glint */}
          <path d="M 36 45 L 40 48" stroke="#fff" strokeWidth="1.2" opacity="0.5">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
          </path>
        </>
      )}

      {/* Bee stripes */}
      <path d="M 26 50 Q 50 44, 74 50" stroke={stripeColor} strokeWidth={stripeW} fill="none" strokeLinecap="round">
        {evo >= 2 && <animate attributeName="opacity" values="1;0.7;1" dur="1.6s" repeatCount="indefinite" />}
      </path>
      <path d="M 26 64 Q 50 70, 74 64" stroke={stripeColor} strokeWidth={stripeW} fill="none" strokeLinecap="round">
        {evo >= 2 && <animate attributeName="opacity" values="1;0.7;1" dur="1.6s" begin="0.8s" repeatCount="indefinite" />}
      </path>
      {/* Center stripe (lv 20+) */}
      {evo >= 2 && (
        <path d="M 28 57 Q 50 57, 72 57" stroke={stripeColor} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.55" />
      )}

      {/* Bubble highlight */}
      <ellipse cx="38" cy="42" rx="6" ry="4" fill="#fff" opacity="0.85" />
      <circle cx="60" cy="38" r="2" fill="#fff" opacity="0.7" />

      {/* Antennae */}
      <path d="M 42 28 Q 40 18, 36 14" stroke="#1e1b16" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <path d="M 58 28 Q 60 18, 64 14" stroke="#1e1b16" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      <circle cx="36" cy="14" r="2.5" fill="#1e1b16" />
      <circle cx="64" cy="14" r="2.5" fill="#1e1b16" />

      {/* Crown (lv 10+) */}
      {evo >= 1 && (
        <g>
          <path d="M 36 18 L 40 10 L 50 16 L 60 10 L 64 18 Z" fill="#FFD700" stroke="#1e1b16" strokeWidth="1.2">
            <animateTransform attributeName="transform" type="translate" values="0,0;0,-1;0,0" dur="1.8s" repeatCount="indefinite" />
          </path>
          {/* Crown gems for legendary */}
          {evo >= 7 && (
            <>
              <circle cx="40" cy="11" r="2" fill="#e74c3c" />
              <circle cx="50" cy="8" r="2.5" fill="#9b59b6" />
              <circle cx="60" cy="11" r="2" fill="#3498db" />
            </>
          )}
        </g>
      )}

      {/* Eyes */}
      {wink ? (
        <path d="M 40 42 Q 43 39, 46 42" stroke="#1e1b16" strokeWidth="2" fill="none" strokeLinecap="round" />
      ) : (
        <g fill={evo >= 7 ? '#9b59b6' : '#1e1b16'}>
          <circle cx="43" cy={eyeY} r="3" />
          <circle cx="44" cy={eyeY - 1} r="1" fill="#fff" />
        </g>
      )}
      <g fill={evo >= 7 ? '#9b59b6' : '#1e1b16'}>
        <circle cx="57" cy={eyeY} r="3" />
        <circle cx="58" cy={eyeY - 1} r="1" fill="#fff" />
      </g>

      {/* Mouth */}
      {mood === 'happy'    && <path d="M 44 64 Q 50 70, 56 64" stroke="#1e1b16" strokeWidth="2" fill="none" strokeLinecap="round" />}
      {mood === 'thinking' && <path d="M 46 67 Q 50 65, 54 67" stroke="#1e1b16" strokeWidth="2" fill="none" strokeLinecap="round" />}
      {mood === 'cheer'    && <ellipse cx="50" cy="66" rx="3" ry="3" fill="#1e1b16" />}

      {/* Blush */}
      <ellipse cx="36" cy="58" rx="3" ry="2" fill="#e8a87c" opacity="0.7" />
      <ellipse cx="64" cy="58" rx="3" ry="2" fill="#e8a87c" opacity="0.7" />
    </svg>
  );
};

export default Fizzbuzz;
